import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";

export type LanguageCode = "en" | "pcm" | "yo" | "ha" | "ig";

export interface LanguageOption {
  code: LanguageCode;
  name: string;
  nativeName: string;
}

export const LANGUAGES: LanguageOption[] = [
  { code: "en", name: "English", nativeName: "English (NG)" },
  { code: "pcm", name: "Nigerian Pidgin", nativeName: "Naija Pidgin" },
  { code: "yo", name: "Yoruba", nativeName: "Èdè Yorùbá" },
  { code: "ha", name: "Hausa", nativeName: "Harshen Hausa" },
  { code: "ig", name: "Igbo", nativeName: "Asụsụ Igbo" },
];

// Helper to persist cross-subdomain cookies (*.flamesnova.name.ng)
export function setCrossDomainCookie(name: string, value: string) {
  if (typeof document === "undefined") return;
  try {
    const host = window.location.hostname;
    const isCustomDomain = host.includes("flamesnova.name.ng");
    const domainPart = isCustomDomain ? "; domain=.flamesnova.name.ng" : "";
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=31536000; SameSite=Lax${domainPart}`;
  } catch (err) {
    // Ignore restricted cookie environments
  }
}

export function getCrossDomainCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  try {
    const match = document.cookie.match(new RegExp("(^|;\\s*)" + name + "=([^;]+)"));
    return match ? decodeURIComponent(match[2]) : null;
  } catch (err) {
    return null;
  }
}

export const TRANSLATIONS: Record<LanguageCode, Record<string, string>> = {
  en: {
    // Navigation & Global
    "nav.vtu": "VTU Telecom",
    "nav.ai": "Flames AI",
    "nav.store": "FN Atelier",
    "nav.docs": "Developer Docs",
    "nav.about": "About Ecosystem",
    "nav.signin": "Sign In",
    "nav.launcher": "Transformed Tribe Apps",
    "nav.ecosystem": "Ecosystem",
    "nav.coming_soon": "Coming soon",
    "nav.current": "Current product",

    // Common Actions
    "common.login": "Log In",
    "common.register": "Create Account",
    "common.dashboard": "Dashboard",
    "common.get_started": "Get Started",
    "common.learn_more": "Learn More",
    "common.terms": "Terms of Service",
    "common.privacy": "Privacy Policy",
    "common.compliance": "Compliance & CAC",
    "common.security": "Security Policy",
    "common.refund": "Refund Policy",

    // Hero & Tagline
    "hero.badge": "TRANSFORMED TRIBE INC. · MULTI-PRODUCT DIGITAL ECOSYSTEM",
    "hero.title_pre": "Intelligent Infrastructure for Africa's",
    "hero.title_accent": "Digital Economy",
    "hero.subtitle": "A unified technology foundation delivering automated high-speed telecommunications, frontier AI reasoning models, luxury precision hardware, and open autonomous agent commerce protocols.",
    "hero.cta_vtu": "Launch VTU Telecom",
    "hero.cta_ai": "Open Flames Nova AI",
    "hero.cta_specs": "Protocol Specs",

    // Section Titles
    "section.subdomains_badge": "Dedicated Subdomains",
    "section.subdomains_title": "Explore Our Autonomous Product Suite",
    "section.subdomains_desc": "Each product operates with its own specialized domain architecture, dedicated brand identity, and machine-readable agent protocols under TT Inc.",
    "section.filter_all": "All Platforms",
    "section.filter_telecom": "Telecom & VTU",
    "section.filter_ai": "Frontier AI",
    "section.filter_store": "Hardware Store",
    "section.filter_protocols": "Protocols & APIs",

    // Interactive Demo
    "demo.title": "Live Ecosystem Experience",
    "demo.subtitle": "Interactive tools running in real-time on our edge network",
    "demo.calculator_tab": "Telecom Rate Checker",
    "demo.ai_tab": "Apex R1 Reasoning Preview",
    "demo.network_tab": "Live Network Health",

    // VTU Content
    "vtu.badge": "SUB-SECOND AUTOMATED VENDING PLATFORM",
    "vtu.hero_title": "Cheapest High-Speed Data & Instant Airtime in Nigeria",
    "vtu.hero_subtitle": "Automated telecom infrastructure delivering MTN, Airtel, Glo, and 9mobile bundles in less than 3 seconds. Electricity tokens, cable subscriptions, and developer APIs.",
    "vtu.rates": "Live Telecom Rates",
    "vtu.services": "Automated Services",
    "vtu.get_started": "Start Vending Free",
    "vtu.api_docs": "VTU API Specs",

    // AI Content
    "ai.badge": "AUTONOMOUS REASONING & MULTILINGUAL INTELLIGENCE",
    "ai.hero_title": "Nigeria's Frontier Multilingual AI Assistant",
    "ai.hero_subtitle": "Equipped with Apex R1 step-by-step reasoning, real-time web research, native Nigerian languages, live code canvas sandbox, and Model Context Protocol (MCP).",
    "ai.start_chat": "Launch AI Studio",
    "ai.explore_models": "Explore Models",

    // Store Content
    "store.badge": "THE FN ATELIER EDIT",
    "store.hero_title": "Technology, considered.",
    "store.hero_subtitle": "A considered edit of personal technology, precision audio and everyday essentials. Carefully selected, clearly described and delivered nationwide.",
    "store.search_placeholder": "Search phones, laptops, chargers, audio, gadgets...",
    "store.shop_now": "Explore Collection",
    "store.wholesale": "Wholesale & Bulk Orders",

    // Footer
    "footer.desc": "Nigeria's premier multi-product technology ecosystem. High-speed telecom vending, frontier AI models, and luxury precision hardware operated by TT (Transformed Tribe) Inc.",
    "footer.founder": "Founder: Agbasionwe Emmanuel Chiemelie",
    "footer.subdomains": "Subdomains",
    "footer.protocols": "Protocols",
    "footer.legal": "Legal & Governance",
    "footer.rights": "© 2026 TT (Transformed Tribe) Inc. All rights reserved.",
    "footer.appearance": "Appearance & Locale",
    "footer.theme_light": "Light Theme Active",
    "footer.theme_dark": "Dark Theme Active",
  },
  pcm: {
    // Navigation & Global
    "nav.vtu": "VTU Telecom",
    "nav.ai": "Flames AI",
    "nav.store": "FN Atelier Store",
    "nav.docs": "Dev Docs",
    "nav.about": "About Us",
    "nav.signin": "Enter Account",
    "nav.launcher": "Transformed Tribe Apps",
    "nav.ecosystem": "Ecosystem",
    "nav.coming_soon": "E dey come",
    "nav.current": "Where you dey",

    // Common Actions
    "common.login": "Enter Account",
    "common.register": "Create Account",
    "common.dashboard": "Dashboard",
    "common.get_started": "Start Am Now",
    "common.learn_more": "Read More",
    "common.terms": "Terms of Service",
    "common.privacy": "Privacy Rules",
    "common.compliance": "Company Law & CAC",
    "common.security": "Security Policy",
    "common.refund": "Refund Rules",

    // Hero & Tagline
    "hero.badge": "TRANSFORMED TRIBE INC. · FULL DIGITAL ECOSYSTEM",
    "hero.title_pre": "Beta Infrastructure for Africa",
    "hero.title_accent": "Digital Market",
    "hero.subtitle": "One solid system for sharp-sharp data, airtime, deep AI wey sabi Nigerian business, luxury original gadgets, and developer tools.",
    "hero.cta_vtu": "Buy Cheap Data & Airtime",
    "hero.cta_ai": "Open Flames AI",
    "hero.cta_specs": "Check Protocols",

    // Section Titles
    "section.subdomains_badge": "Subdomain Dem",
    "section.subdomains_title": "Check Our Main Products",
    "section.subdomains_desc": "Every single service get its own clean subdomain, high speed, and direct integration under TT Inc.",
    "section.filter_all": "Everything",
    "section.filter_telecom": "Data & Bills",
    "section.filter_ai": "Flames AI",
    "section.filter_store": "Gadgets Store",
    "section.filter_protocols": "APIs & Tech",

    // Interactive Demo
    "demo.title": "Try Am Live Now",
    "demo.subtitle": "Check rates and test our system with zero stress",
    "demo.calculator_tab": "Check Data Price",
    "demo.ai_tab": "Test AI Reasoning",
    "demo.network_tab": "Network Condition",

    // VTU Content
    "vtu.badge": "SHARP-SHARP DATA VENDING PLATFORM",
    "vtu.hero_title": "Cheapest High-Speed Data & Instant Airtime for Naija",
    "vtu.hero_subtitle": "Fast telecom engine wey dey deliver MTN, Airtel, Glo, and 9mobile under 3 seconds. Light token, DSTV, GOTV and developer API.",
    "vtu.rates": "Current Data Rates",
    "vtu.services": "Our Full Services",
    "vtu.get_started": "Start Free",
    "vtu.api_docs": "VTU API Specs",

    // AI Content
    "ai.badge": "AI WEY SABI NAIJA BUSINESS & CODE",
    "ai.hero_title": "Sharp Multilingual AI Assistant for Nigeria",
    "ai.hero_subtitle": "E sabi think step by step with Apex R1, do live search online, talk Pidgin, Yoruba, Hausa and Igbo, and write code inside live browser preview.",
    "ai.start_chat": "Start Chat Now",
    "ai.explore_models": "Check AI Models",

    // Store Content
    "store.badge": "FN ATELIER GADGETS",
    "store.hero_title": "Original Technology Wey Pure.",
    "store.hero_subtitle": "Carefully selected phones, original audio, 65W GaN fast chargers and gadgets wey get warranty. We dey deliver everywhere for Naija.",
    "store.search_placeholder": "Find phone, laptop, charger, acoustic earpiece...",
    "store.shop_now": "Check Store",
    "store.wholesale": "Wholesale & Bulk Orders",

    // Footer
    "footer.desc": "Top tech platform for Naija. Sharp telecom vending, strong AI models, and original gadgets operated by TT (Transformed Tribe) Inc.",
    "footer.founder": "Founder: Agbasionwe Emmanuel Chiemelie",
    "footer.subdomains": "Our Platforms",
    "footer.protocols": "Protocols",
    "footer.legal": "Legal & Rules",
    "footer.rights": "© 2026 TT (Transformed Tribe) Inc. All rights reserved.",
    "footer.appearance": "Theme & Language",
    "footer.theme_light": "White Mode Dey On",
    "footer.theme_dark": "Dark Mode Dey On",
  },
  yo: {
    // Navigation & Global
    "nav.vtu": "VTU Telecom",
    "nav.ai": "Flames AI",
    "nav.store": "FN Atelier",
    "nav.docs": "Iwe Imọ-ẹrọ",
    "nav.about": "Nipa Wa",
    "nav.signin": "Wọle",
    "nav.launcher": "Awọn Ohun elo TT",
    "nav.ecosystem": "Eto Gbogbogbo",
    "nav.coming_soon": "N bọ laipẹ",
    "nav.current": "Ibi ti o wa",

    // Common Actions
    "common.login": "Wọle",
    "common.register": "Ṣẹda Account",
    "common.dashboard": "Pẹpẹ Iṣakoso",
    "common.get_started": "Bẹrẹ Bayi",
    "common.learn_more": "Ka Siwaju",
    "common.terms": "Awọn Ilana Lilo",
    "common.privacy": "Ilana Aṣiri",
    "common.compliance": "Iforukọsilẹ CAC & Ofin",
    "common.security": "Aabo Platform",
    "common.refund": "Ilana Idapada",

    // Hero & Tagline
    "hero.badge": "TRANSFORMED TRIBE INC. · ETO IMỌ-ẸRỌ TI ILẸ AFIRIKA",
    "hero.title_pre": "Imọ-ẹrọ To Dangajia fun",
    "hero.title_accent": "Ọrọ-aje Digital",
    "hero.subtitle": "Eto igbalode ti o lagbara fun rira data olowo poku, airtime lẹsẹkẹsẹ, AI to mọ ilana Naijiria, ati awọn ohun elo didara julọ.",
    "hero.cta_vtu": "Bẹrẹ VTU Telecom",
    "hero.cta_ai": "Ṣii Flames Nova AI",
    "hero.cta_specs": "Awọn Ilana Imọ-ẹrọ",

    // Section Titles
    "section.subdomains_badge": "Awọn Oju-iwe Aladani",
    "section.subdomains_title": "Ṣawari Awọn Ọja Wa",
    "section.subdomains_desc": "Ọja kọọkan n ṣiṣẹ pẹlu orukọ domain tirẹ, iyara to ga, ati aabo pipe labẹ TT Inc.",
    "section.filter_all": "Gbogbo Ọja",
    "section.filter_telecom": "Data & Owo",
    "section.filter_ai": "Oye AI",
    "section.filter_store": "Ile Itaja",
    "section.filter_protocols": "Ilana APIs",

    // Interactive Demo
    "demo.title": "Idanwo Lẹsẹkẹsẹ",
    "demo.subtitle": "Ṣayẹwo iye owo ki o ṣe idanwo lori ayelujara",
    "demo.calculator_tab": "Iye Data",
    "demo.ai_tab": "Idanwo AI",
    "demo.network_tab": "Ipò Nẹtiwọki",

    // VTU Content
    "vtu.badge": "PẸPẸ RIRA DATA TI O YARA JÙLỌ",
    "vtu.hero_title": "Data Olowo Pọku ati Kaadi Oku Lẹsẹkẹsẹ ni Naijiria",
    "vtu.hero_subtitle": "Eto adaṣe to n pin MTN, Airtel, Glo ati 9mobile labẹ iṣẹju-aaya mẹta. Rira ina mọnamọna, DSTV, GOTV ati APIs.",
    "vtu.rates": "Iye Owo Data",
    "vtu.services": "Awọn Iṣẹ Wa",
    "vtu.get_started": "Bẹrẹ Lọfẹẹ",
    "vtu.api_docs": "Ilana VTU API",

    // AI Content
    "ai.badge": "OYE AI TI O MỌ EDE ATUBỌTAN",
    "ai.hero_title": "Oluranlọwọ AI To Dangajia ni Naijiria",
    "ai.hero_subtitle": "Ni ipese pẹlu ironu igbese-nipasẹ-igbese Apex R1, iwadii lori ayelujara, ede Yoruba, Hausa ati Igbo, ati koodu lori ayelujara.",
    "ai.start_chat": "Ṣii AI Studio",
    "ai.explore_models": "Wo Awọn Awoṣe AI",

    // Store Content
    "store.badge": "AWỌN OHUN ELO FN ATELIER",
    "store.hero_title": "Imọ-ẹrọ To Yẹ Ni Ṣiṣe.",
    "store.hero_subtitle": "Awọn foonu gidi, ohun elo orin ti o dangajia, ṣaja GaN to yara, ati fifiranṣẹ kaakiri orilẹ-ede Naijiria.",
    "store.search_placeholder": "Wa foonu, kọmputa, ṣaja, agbekọri...",
    "store.shop_now": "Wo Ile Itaja",
    "store.wholesale": "Rira Ni Pọpọ",

    // Footer
    "footer.desc": "Eto imọ-ẹrọ ti o gbajumọ julọ ni Naijiria. Data iyara, AI to peye, ati awọn ẹrọ igbalode ti TT Inc n ṣakoso.",
    "footer.founder": "Oludasile: Agbasionwe Emmanuel Chiemelie",
    "footer.subdomains": "Awọn Ẹka Wa",
    "footer.protocols": "Awọn Ilana",
    "footer.legal": "Ofin & Aabo",
    "footer.rights": "© 2026 TT (Transformed Tribe) Inc. Gbogbo ẹtọ ni a daabobo.",
    "footer.appearance": "Irisi & Èdè",
    "footer.theme_light": "Irisi Funfun Nṣiṣẹ",
    "footer.theme_dark": "Irisi Dudu Nṣiṣẹ",
  },
  ha: {
    // Navigation & Global
    "nav.vtu": "VTU Telecom",
    "nav.ai": "Flames AI",
    "nav.store": "FN Atelier",
    "nav.docs": "Bayanin Fasaha",
    "nav.about": "Game da Mu",
    "nav.signin": "Shiga Ciki",
    "nav.launcher": "Manhajojin TT",
    "nav.ecosystem": "Tsarin Fasaha",
    "nav.coming_soon": "Yana Nan Tafe",
    "nav.current": "Inda Kake",

    // Common Actions
    "common.login": "Shiga Ciki",
    "common.register": "Bude Asusu",
    "common.dashboard": "Dandalin Sarrafa",
    "common.get_started": "Fara Yanzu",
    "common.learn_more": "Karin Bayani",
    "common.terms": "Sharuɗɗan Aiki",
    "common.privacy": "Kariyar Bayanai",
    "common.compliance": "Rijistar CAC da Doka",
    "common.security": "Kariyar Tsaro",
    "common.refund": "Dokar Mayar da Kudi",

    // Hero & Tagline
    "hero.badge": "TRANSFORMED TRIBE INC. · INGANTACCEN TSARIN FASAHAR NAJERIYA",
    "hero.title_pre": "Kayan Aikin Fasaha Na Zamani Don",
    "hero.title_accent": "Tattalin Arzikin Dijital",
    "hero.subtitle": "Hanyar samun data mai sauƙi da sauri, kiran waya, basirar AI mai gane kasuwancin Najeriya, da ingantattun kayan aiki.",
    "hero.cta_vtu": "Fara Amfani Da VTU",
    "hero.cta_ai": "Bude Flames AI",
    "hero.cta_specs": "Ka'idojin Fasaha",

    // Section Titles
    "section.subdomains_badge": "Shafukan Musamman",
    "section.subdomains_title": "Bincika Manhajojinmu",
    "section.subdomains_desc": "Kowace manhaja tana aiki da nata shafin yanar gizo mai sauri karkashin kamfanin TT Inc.",
    "section.filter_all": "Dukkan Manhajoji",
    "section.filter_telecom": "Data da Kudi",
    "section.filter_ai": "Basirar AI",
    "section.filter_store": "Kantin Kayan Aiki",
    "section.filter_protocols": "Ka'idojin API",

    // Interactive Demo
    "demo.title": "Gwajin Kai Tsaye",
    "demo.subtitle": "Duba farashin data da sauran ayyuka nan take",
    "demo.calculator_tab": "Farashin Data",
    "demo.ai_tab": "Gwajin AI",
    "demo.network_tab": "Lafiyar Hanyar Sadarwa",

    // VTU Content
    "vtu.badge": "MANHAJAR SAYAR DA DATA CIKIN SAURI",
    "vtu.hero_title": "Mafi Saukin Farashin Data da Katin Waya a Najeriya",
    "vtu.hero_subtitle": "Tsarin kwamfuta mai tura data MTN, Airtel, Glo, da 9mobile cikin kasa da dakika 3. Katin wutar lantarki, DSTV, GOTV da hanyoyin API.",
    "vtu.rates": "Farashin Data Yanzu",
    "vtu.services": "Ayyukanmu",
    "vtu.get_started": "Fara Kyauta",
    "vtu.api_docs": "Bayanin VTU API",

    // AI Content
    "ai.badge": "BASIRAR AI MAI HARSUNA DA DAMA",
    "ai.hero_title": "Ingantacciyar Basirar AI a Najeriya",
    "ai.hero_subtitle": "Tana aiki da fasahar Apex R1, binciken yanar gizo kai tsaye, harsunan Hausa, Yoruba da Igbo, da rubuta kodi kai tsaye a shafi.",
    "ai.start_chat": "Fara Hira Yanzu",
    "ai.explore_models": "Duba Nau'o'in AI",

    // Store Content
    "store.badge": "KAYAN FASAHAR FN ATELIER",
    "store.hero_title": "Ingantattun Kayan Fasaha Na Asali.",
    "store.hero_subtitle": "Wayoyi na asali, belun kunne masu inganci, caja mai sauri ta GaN, da aikawa zuwa kowace jiha a Najeriya.",
    "store.search_placeholder": "Nemi waya, kwamfuta, caja, belun kunne...",
    "store.shop_now": "Shiga Shagon",
    "store.wholesale": "Sayen Sari Mai Rahusa",

    // Footer
    "footer.desc": "Babban tsarin fasaha a Najeriya. Sayar da data cikin sauri, fasahar AI, da kayan fasaha na musamman karkashin TT Inc.",
    "footer.founder": "Wanda ya kafa: Agbasionwe Emmanuel Chiemelie",
    "footer.subdomains": "Shafukanmu",
    "footer.protocols": "Ka'idoji",
    "footer.legal": "Sharuɗɗa da Dokoki",
    "footer.rights": "© 2026 TT (Transformed Tribe) Inc. An kiyaye duk haƙƙoƙi.",
    "footer.appearance": "Kalar Shafin & Yare",
    "footer.theme_light": "Farar Kalar Tana Aiki",
    "footer.theme_dark": "Bakar Kalar Tana Aiki",
  },
  ig: {
    // Navigation & Global
    "nav.vtu": "VTU Telecom",
    "nav.ai": "Flames AI",
    "nav.store": "FN Atelier",
    "nav.docs": "Akwụkwọ Nka na Ụzụ",
    "nav.about": "Maka Anyị",
    "nav.signin": "Banye",
    "nav.launcher": "Ngwa TT Nile",
    "nav.ecosystem": "Usoro Nka na Ụzụ",
    "nav.coming_soon": "Ọ na-abịa n'oge adịghị anya",
    "nav.current": "Ebe ị nọ ugbu a",

    // Common Actions
    "common.login": "Banye",
    "common.register": "Mepụta Akaụntụ",
    "common.dashboard": "Dashboard",
    "common.get_started": "Bido Ugbu A",
    "common.learn_more": "Mụtakwuo",
    "common.terms": "Usoro Ojiji",
    "common.privacy": "Iwu Nzuzo",
    "common.compliance": "Ndebanye Aha CAC & Iwu",
    "common.security": "Nchekwa Usoro",
    "common.refund": "Iwu Nkwụghachi Ụgwọ",

    // Hero & Tagline
    "hero.badge": "TRANSFORMED TRIBE INC. · USORO NKA NA ỤZỤ NIGERIA",
    "hero.title_pre": "Akụrụngwa Ọgbara Ọhụrụ Maka",
    "hero.title_accent": "Akụ na Ụba Dijitalụ",
    "hero.subtitle": "Usoro a pụrụ ịtụkwasị obi maka ịzụ data dị ọnụ ala, kaadị oku ozugbo, ọgụgụ isi AI na-aghọta azụmahịa Nigeria, na ngwa eletriki pụrụ iche.",
    "hero.cta_vtu": "Bido VTU Telecom",
    "hero.cta_ai": "Mepee Flames AI",
    "hero.cta_specs": "Usoro Protocol",

    // Section Titles
    "section.subdomains_badge": "Ngalaba Pụrụ Iche",
    "section.subdomains_title": "Chọpụta Ngwa Anyị",
    "section.subdomains_desc": "Ngwa ọ bụla nwere ngalaba nke aka ya, ọsọ dị egwu, yana nchekwa zuru oke n'okpuru TT Inc.",
    "section.filter_all": "Ngwa Nile",
    "section.filter_telecom": "Data & Ịkwụ Ụgwọ",
    "section.filter_ai": "Ọgụgụ Isi AI",
    "section.filter_store": "Ụlọ Ahịa Ngwa",
    "section.filter_protocols": "Usoro API",

    // Interactive Demo
    "demo.title": "Nnwale Ozugbo",
    "demo.subtitle": "Nwalee ngwa anyị ma lelee ọnụahịa data n'efu",
    "demo.calculator_tab": "Ọnụahịa Data",
    "demo.ai_tab": "Nnwale AI",
    "demo.network_tab": "Ọnọdụ Netwọk",

    // VTU Content
    "vtu.badge": "NGWA NKESA DATA KACHASỊ ỌSỌ",
    "vtu.hero_title": "Data Dị Ọnụ Ala na Kaadị Oku Ozugbo na Nigeria",
    "vtu.hero_subtitle": "Usoro na-ebuga MTN, Airtel, Glo na 9mobile n'ime sekọnd 3. Kaadị ọkụ eletrik, DSTV, GOTV na usoro API ndị nrụpụta.",
    "vtu.rates": "Ọnụahịa Data Ugbu A",
    "vtu.services": "Ọrụ Anyị Nile",
    "vtu.get_started": "Bido n'Efu",
    "vtu.api_docs": "Nkọwapụta VTU API",

    // AI Content
    "ai.badge": "ỌGỤGỤ ISI AI NA-ASỤ ASỤSỤ NIGERIA",
    "ai.hero_title": "Ezigbo Onye Enyemaka AI na Nigeria",
    "ai.hero_subtitle": "Ngwa na-eche echiche site na Apex R1, nyocha ịntanetị ozugbo, asụsụ Igbo, Yoruba na Hausa, yana ịmepụta koodu n'ime ihe nchọgharị.",
    "ai.start_chat": "Bido AI Studio",
    "ai.explore_models": "Lelee Awoṣe AI",

    // Store Content
    "store.badge": "NGWA ELETRIKI FN ATELIER",
    "store.hero_title": "Teknụzụ nke Ezigbo Ọdịmma.",
    "store.hero_subtitle": "Ezigbo ekwentị, ngwa egwu pụrụ iche, chaja GaN na-agba ọsọ, yana mbupu na steeti niile na Nigeria.",
    "store.search_placeholder": "Chọọ ekwentị, laptọọpụ, chaja, earpiece...",
    "store.shop_now": "Chọpụta Ụlọ Ahịa",
    "store.wholesale": "Ịzụ Ahịa n'Ọnụ Ọgụgụ Buru Ibu",

    // Footer
    "footer.desc": "Usoro teknụzụ kacha elu na Nigeria. Nkesa data ngwa ngwa, ngwa AI, na ngwongwo pụrụ iche n'okpuru TT Inc.",
    "footer.founder": "Onye Tọrọ Ntọala: Agbasionwe Emmanuel Chiemelie",
    "footer.subdomains": "Ngalaba Anyị",
    "footer.protocols": "Usoro Protocol",
    "footer.legal": "Iwu & Nchekwa",
    "footer.rights": "© 2026 TT (Transformed Tribe) Inc. Ikike niile echekwara.",
    "footer.appearance": "Ọdịdị & Asụsụ",
    "footer.theme_light": "Agba Ọcha Na-arụ Ọrụ",
    "footer.theme_dark": "Agba Ojii Na-arụ Ọrụ",
  },
};

interface LanguageContextType {
  language: LanguageCode;
  setLanguage: (code: LanguageCode) => void;
  t: (key: string, fallback?: string) => string;
}

const LanguageContext = createContext<LanguageContextType>({
  language: "en",
  setLanguage: () => {},
  t: (key, fallback) => fallback || key,
});

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<LanguageCode>(() => {
    if (typeof window !== "undefined") {
      // 1. Cross-domain cookie first
      const cookieLang = getCrossDomainCookie("flames_lang") as LanguageCode;
      if (cookieLang && TRANSLATIONS[cookieLang]) return cookieLang;

      // 2. Local storage second
      const saved = localStorage.getItem("flames_lang") as LanguageCode;
      if (saved && TRANSLATIONS[saved]) return saved;
    }
    return "en";
  });

  const setLanguage = (code: LanguageCode) => {
    setLanguageState(code);
    if (typeof window !== "undefined") {
      localStorage.setItem("flames_lang", code);
      setCrossDomainCookie("flames_lang", code);
      document.documentElement.lang = code;

      // Also set Google Translate cookie if Google Translate is active
      try {
        const gtLang = code === "pcm" ? "en" : code;
        document.cookie = `googtrans=/en/${gtLang}; path=/; domain=${window.location.hostname}`;
      } catch (e) {
        // Ignore cookie errors
      }

      window.dispatchEvent(new CustomEvent("flames:languageChange", { detail: { lang: code } }));
    }
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      document.documentElement.lang = language;
    }

    // Cross-tab synchronization
    const handleStorage = (e: StorageEvent) => {
      if (e.key === "flames_lang" && e.newValue && TRANSLATIONS[e.newValue as LanguageCode]) {
        setLanguageState(e.newValue as LanguageCode);
      }
    };

    // Custom event synchronization
    const handleCustomEvent = (e: Event) => {
      const customEvent = e as CustomEvent<{ lang: LanguageCode }>;
      if (customEvent.detail && TRANSLATIONS[customEvent.detail.lang]) {
        setLanguageState(customEvent.detail.lang);
      }
    };

    window.addEventListener("storage", handleStorage);
    window.addEventListener("flames:languageChange", handleCustomEvent);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("flames:languageChange", handleCustomEvent);
    };
  }, [language]);

  const t = (key: string, fallback?: string): string => {
    const langDict = TRANSLATIONS[language];
    if (langDict && langDict[key]) {
      return langDict[key];
    }
    // Fallback to English dictionary
    if (TRANSLATIONS.en[key]) {
      return TRANSLATIONS.en[key];
    }
    // Final fallback to provided fallback text or the key itself
    return fallback !== undefined ? fallback : key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
